import { FC } from "react";
import "./CircularProgress.css";

/**
 * Props for the CircularProgress component
 */
export interface CircularProgressProps {
  /**
   * Progress value between 0 and 1 (inclusive)
   * - 0 represents 0% completion
   * - 1 represents 100% completion
   * - Values are automatically clamped to this range
   */
  progress: number;

  /**
   * Diameter of the circular progress indicator in pixels
   * @default 20
   */
  size?: number;

  /**
   * Width of the progress stroke in pixels
   * @default 3
   */
  strokeWidth?: number;

  /**
   * Additional CSS class name to apply to the component
   */
  className?: string;

  /**
   * Accessible label for screen readers
   * @default "Loading progress"
   */
  ariaLabel?: string;
}

/**
 * CircularProgress Component
 *
 * A circular progress indicator that visually represents completion status.
 * The progress fills clockwise from the top (12 o'clock position).
 *
 * Features:
 * - Uses design system color tokens for consistent theming
 * - Automatically clamps progress values to valid range (0-1)
 * - Fully accessible with proper ARIA attributes
 * - Smooth visual transitions when progress changes
 * - Responsive sizing with configurable dimensions
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CircularProgress progress={0.75} />
 *
 * // Custom size and stroke width
 * <CircularProgress
 *   progress={0.5}
 *   size={40}
 *   strokeWidth={4}
 * />
 *
 * // With custom accessibility label
 * <CircularProgress
 *   progress={downloadProgress}
 *   ariaLabel="Download progress"
 * />
 * ```
 */
export const CircularProgress: FC<CircularProgressProps> = ({
  progress,
  size = 20,
  strokeWidth = 3,
  className = "",
  ariaLabel = "Loading progress",
}) => {
  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Clamp progress to valid range [0, 1]
  const normalizedProgress = Math.min(Math.max(progress, 0), 1);

  // Calculate stroke offset for progress visualization
  const strokeDashoffset = circumference * (1 - normalizedProgress);

  // Convert progress to percentage for accessibility
  const progressPercentage = Math.round(normalizedProgress * 100);

  return (
    <svg
      width={size}
      height={size}
      className={`indigo-circular-progress ${className}`}
      role="progressbar"
      aria-valuenow={progressPercentage}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      data-testid="circular-progress"
    >
      {/* Background track circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        className="indigo-circular-progress__track"
        strokeWidth={strokeWidth}
        fill="transparent"
      />

      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        className="indigo-circular-progress__progress"
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={
          {
            // CSS custom properties for dynamic values
            "--progress-circumference": circumference,
            "--progress-offset": strokeDashoffset,
          } as React.CSSProperties
        }
      />
    </svg>
  );
};

export default CircularProgress;
